import cx from "classnames";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";

import { GiftCardPreviewImage } from "@/modules/giftCards";
import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";
import { ShopDisplayType } from "api/types";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { TextButton } from "atoms/text-button/TextButton";
import { IfFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";

export interface CartItemProps {
  id: string;
  imgUrl?: string | null;
  type: number;
  title?: string | null;
  discount: number;
  price: number;
  packageTitle: string;
  showEditButton: boolean;
  url: string;
  onClickUrl?: () => void;
  expiresOn: Dayjs | null;
  expired: boolean;
  onEdit: () => void;
  onDelete: () => void;
  locationName?: string;
  fulfillmentType?: string | null;
  giftCardRecipientMissing?: boolean;
}

const Subtitle = (props: {
  expired: boolean;
  expiresOn: Dayjs | null;
  subtitle: string;
  giftCardRecipientMissing?: boolean;
  onClickUrl?: () => void;
}) => {
  if (props.giftCardRecipientMissing && props.onClickUrl) {
    return (
      <span
        className={
          "flex items-center justify-center rounded bg-[#FF7979]/10 px-1.5 py-1 font-sans text-chip uppercase text-red"
        }
        onClick={(e) => {
          if (props.onClickUrl) {
            e.preventDefault();
            props.onClickUrl();
          }
        }}
      >
        Needs recipient
      </span>
    );
  }
  if (props.expired && props.expiresOn) {
    return (
      <span className="text-bold2 text-red">
        Expired on {props.expiresOn.format("ll")}
      </span>
    );
  } else if (props.expired) {
    return (
      <span className="text-bold2 text-red">Expired, no longer available</span>
    );
  } else if (props.expiresOn) {
    return (
      <span className="text-body2 text-red">
        Available for purchase until {props.expiresOn.format("ll")}
      </span>
    );
  } else {
    return <span className="text-body2 text-secondary">{props.subtitle}</span>;
  }
};

export const CartItem = ({
  imgUrl,
  type,
  title,
  price,
  packageTitle,
  showEditButton,
  discount,
  expired,
  expiresOn,
  url,
  onClickUrl,
  onEdit,
  onDelete,
  locationName,
  fulfillmentType,
  giftCardRecipientMissing,
}: CartItemProps) => {
  const shopDisplayTypeDisplayMap: Record<ShopDisplayType, string> = {
    [ShopDisplayType.Membership]: "Membership",
    [ShopDisplayType.Treatment]: "Treatment",
    [ShopDisplayType.Package]: "Package",
    [ShopDisplayType.Product]: "Product",
    [ShopDisplayType.ProductFromExternalSource]: "Product",
    [ShopDisplayType.GiftCard]: "Gift Card",
  };
  const typeName = shopDisplayTypeDisplayMap[type as ShopDisplayType];
  const isMembership = type === ShopDisplayType.Membership;
  const hasDiscount = Boolean(discount && discount > 0);
  const [isLoading, setIsLoading] = useState(false);
  const { notification } = useHaptics();

  return (
    <div
      className={cx(
        "flex",
        "cursor-pointer",
        "items-center",
        isMembership ? "bg-brand-tint" : "bg-one",
        "pt-4",
        "pl-4",
        fulfillmentType && "pb-4"
      )}
    >
      {!!imgUrl && (
        <Link
          className="flex h-[72px] flex-col items-center gap-1 self-center"
          to={url}
          onClick={(e) => {
            if (onClickUrl) {
              e.preventDefault();
              onClickUrl();
            }
          }}
        >
          {type === ShopDisplayType.GiftCard ? (
            <div className="h-[56px] w-[72px]">
              <GiftCardPreviewImage backgroundImage={imgUrl} size="icon" />
            </div>
          ) : (
            <img
              className="-mt-2 h-[72px] w-[72px] items-center rounded object-cover"
              src={imgUrl}
              height={72}
              alt={typeName}
              loading="lazy"
            />
          )}
          {fulfillmentType && (
            <div>
              <DescriptiveTag
                color="custom"
                customColor={"brand-color"}
                size="small"
              >
                {fulfillmentType}
              </DescriptiveTag>
            </div>
          )}
        </Link>
      )}
      <div className={cx("pl-4", imgUrl ? "w-[80%]" : "w-[100%]")}>
        <Link
          className="flex items-baseline justify-between"
          to={url}
          onClick={(e) => {
            if (onClickUrl) {
              e.preventDefault();
              onClickUrl();
            }
          }}
        >
          <div className="pr-4">
            <div className="line-clamp-2 font-sans text-bold1">{title}</div>
            <div className="my-1.5">
              <DescriptiveTag color="secondary" size="small">
                {typeName}
              </DescriptiveTag>
            </div>
          </div>
          <div className="flex flex-col pr-4 text-right font-sans text-bold2">
            {hasDiscount && <span>{formatCurrency(price - discount)}</span>}
            <span
              className={
                hasDiscount ? "font-thin text-secondary line-through" : ""
              }
            >
              {formatCurrency(price)}
            </span>
          </div>
        </Link>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <div className="flex flex-col pb-4">
              <IfFlag flag="selectPatientLocation">
                {locationName && (
                  <div className="text-xs text-red">
                    <span className="font-small">Not available at the </span>
                    <span className="font-medium">{locationName}</span>
                    <span className="font-small"> location</span>
                  </div>
                )}
              </IfFlag>
              <div className="font-sans">
                <Subtitle
                  expired={expired}
                  expiresOn={expiresOn}
                  subtitle={packageTitle}
                  giftCardRecipientMissing={giftCardRecipientMissing}
                  onClickUrl={onClickUrl}
                />
              </div>
            </div>
            <div className="items-bottom -mt-3 flex justify-between pr-4">
              {showEditButton && (
                <TextButton onClick={onEdit} xsText>
                  EDIT
                </TextButton>
              )}
              <TextButton
                xsText
                disabled={isLoading}
                onClick={async () => {
                  setIsLoading(true);
                  await notification("error").catch(() => undefined);
                  onDelete();
                }}
              >
                REMOVE
              </TextButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
